<template>
  <div class="wrapper">
    <div class="tree-view-container" :style="treeViewContainerStyles">
      <tree-view :entries="entries" :entry-state-change="entryStateChange">
        <template v-slot="_">
          <slot :vm="_.vm" :entry="_.entry">{{ _.entry.name }}</slot>
        </template>
      </tree-view>
    </div>

    <div v-if="!disableActions" class="actions">
      <div class="action-component">
        <gl-tree-view-filter
          place-holder="ツリーの絞込"
          :value="filterText"
          @update:value="updateFilterText"
        />
      </div>
      <div
        class="action-component"
        gl-tooltip="ファイル読込"
        gl-tooltip-direction="down"
      >
        <gl-button icon-button @click="$refs.file.click()"
          ><md-icon><font-awesome-icon icon="file-upload"/></md-icon
        ></gl-button>
      </div>
      <div
        class="action-component"
        gl-tooltip="ファイル出力"
        gl-tooltip-direction="down"
      >
        <gl-button icon-button @click="exportAsCsv"
          ><md-icon><font-awesome-icon icon="file-download"/></md-icon
        ></gl-button>
      </div>
      <div
        class="action-component"
        gl-tooltip="クリア"
        gl-tooltip-direction="down"
      >
        <gl-button icon-button @click="clear"
          ><gl-icon-delete></gl-icon-delete>
        </gl-button>
      </div>
    </div>

    <!-- ファイル選択の表示は隠して、他トリガーからファイル選択ダイアログは起動します -->
    <input
      type="file"
      ref="file"
      hidden
      @change="selectByFile($event.target.files)"
    />

    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      <span>{{ snackbar.message }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import TreeView from '@/components/treeview/TreeView.vue'
import download from '@/util/download'
import { readAsText } from '@/util/localfile'

/**
 * TreeViewに機能追加したラッパー
 *
 * 全選択やファイル入出力をサポートしています。
 */
export default {
  components: { TreeView },
  props: {
    /**
     * 絞込やファイル出力などの便利機能を無効化
     */
    disableActions: {
      type: Boolean,
      default: false,
    },
    /**
     * ツリー描画域の高さ
     *
     * 指定しない場合、表示ツリーの大きさに自動調整
     */
    treeHeight: {
      type: Number,
      default: 350,
    },
    /**
     * ツリーに描画するデータ
     */
    entries: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択済エントリーのコードリスト
     */
    selected: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      /**
       * 絞込テキスト
       */
      filterText: '',
      /**
       * 絞込テキスト変更Subject
       */
      filterTextSubject: new Subject(),
      /**
       * Snackbarプロパティ
       */
      snackbar: {
        show: false,
        duration: 1500,
        message: '',
      },
    }
  },
  computed: {
    treeViewContainerStyles() {
      return {
        height: `${this.treeHeight}px`,
      }
    },
  },
  methods: {
    /**
     * 絞り込みテキストの更新
     */
    updateFilterText(v) {
      this.filterText = v
      this.filterTextSubject.next(v)
    },
    /**
     * 指定されたコードを選択済コード一覧に追加する
     */
    addCodes(codes) {
      this.$emit(
        'update:selected',
        Array.from(new Set([...this.selected, ...codes])), // 重複コードの排除も一応やる
      )
    },
    /**
     * 指定されたコードを選択済コード一覧から削除する
     */
    removeCodes(codes) {
      this.$emit(
        'update:selected',
        this.selected.filter(code => !codes.includes(code)),
      )
    },
    /**
     * 指定されたコードでを選択済コード一覧を入れ替える
     */
    replaceCodes(codes) {
      this.$emit('update:selected', codes)
    },
    /**
     * ツリーノードの状態変化時処理
     */
    entryStateChange(entry) {
      if (!entry.children) {
        entry.state === 'checked'
          ? this.addCodes([entry.code])
          : this.removeCodes(entry.code)
      } else {
        // 状態変更が葉ではない場合、状態に応じて子要素の全選択 / 全削除を行う
        // Intermediate状態は通知は来ないので無視してよい（checked / noneのみ）
        const targetCodes = []
        // Collect leaves
        walk(entry, 0, e => {
          if (!e.children && e.visible) {
            targetCodes.push(e.code)
          }
        })

        entry.state === 'checked'
          ? this.addCodes(targetCodes)
          : this.removeCodes(targetCodes)
      }
    },
    /**
     * 指定ファイルに記載のあるコードを選択状態にする
     */
    async selectByFile(files) {
      if (files.length === 0) {
        return
      }

      // TODO 何かしら読込中的な表現？

      const content = await readAsText(files[0])
      const lines = content
        .split('\n')
        .map(v => v.trim())
        .filter(v => v)

      this.selectCodes(lines)

      this.showSnackbar({ duration: 1000, message: '読込完了' })

      // 同じファイルを読み込んだ場合、changeイベントが発生しなくなってしまうので
      // 読込を行ったらクリアする。
      this.$refs.file.value = ''
    },
    /**
     * 指定のコードを選択状態にする
     *
     * 総入れ替えです。
     */
    selectCodes(codes) {
      const newCodes = []

      for (const topLevelElement of this.entries) {
        walk(topLevelElement, 0, c => {
          if (c.children) {
            const childrenCount = c.children.length
            const checkCount = c.children.filter(n => n.state === 'checked')
              .length
            const intermediateCount = c.children.filter(
              n => n.state === 'intermediate',
            ).length
            if (childrenCount === checkCount) {
              c.state = 'checked'
            } else if (checkCount > 0 || intermediateCount > 0) {
              c.state = 'intermediate'
            } else {
              c.state = 'none'
            }
          } else {
            if (codes.includes(c.code)) {
              c.state = 'checked'
              newCodes.push(c.code)
            } else {
              c.state = 'none'
            }
          }
        })
      }

      this.replaceCodes(newCodes)
    },
    /**
     * エントリーの絞込
     */
    filterCodes(filterText) {
      if (!filterText || filterText === '') {
        // 絞込テキストが空になった -> 初期状態（= 全表示 & トップのみオープン）
        for (const topLevelElement of this.entries) {
          walk(topLevelElement, 0, (c, l) => {
            c.visible = true
            c.open = l === 0
          })
        }
      } else {
        for (const topLevelElement of this.entries) {
          walk(topLevelElement, 0, c => {
            // 検索にヒットしているノードしか表示しないので強制的に全ノードオープン
            c.open = true

            if (!c.children) {
              // マッチを確認するのは葉ノードのみ
              c.visible = c.name.indexOf(filterText) > -1
            }

            if (c.children) {
              // 子要素内にヒットしたノードがある場合、親がヒットしていなくとも
              // 表示状態にする。
              c.visible = c.children.some(entry => entry.visible)
            }
          })
        }
      }
    },
    /**
     * CSV出力
     */
    exportAsCsv() {
      const selected = []
      for (const topLevelElement of this.entries) {
        walk(topLevelElement, 0, c => {
          if (c.children) {
            return
          }
          if (c.state === 'checked') {
            selected.push(c.code)
          }
        })
      }

      if (selected.length === 0) {
        this.showSnackbar({ message: '何も選択されていません' })
        return
      }

      download(selected.join('\r\n') + '\r\n', '選択コード一覧.csv')
    },
    // 選択をクリアします。外部からツリーのチェック状態を操作するにはentryの
    // stateを直接操作する必要があります。
    // TODO 条件復元時に、もう存在しないコードが入っているとツリーUI上は何も起きない
    //      にも関わらず条件としては選択状態となってしまう不具合がありました。
    //      本来は存在しなくなったコードなどは随時消さないとな気がしますが難しいため、
    //      暫定でクリア機能を付けます。
    clear() {
      for (const entry of this.entries) {
        walk(entry, 0, e => {
          e.state = 'none'
        })
      }
      this.$emit('update:selected', [])
    },
    /**
     * Snackbarの表示
     */
    showSnackbar(options) {
      this.snackbar.duration = options.duration || 1500
      this.snackbar.message = options.message
      this.snackbar.show = true
    },
  },
  mounted() {
    // エントリー名絞込のトリガーです
    // 絞込テキストの入力があるとfilterTextSubjectに通知をするので、
    // 適度なタイミング（xxx ms入力が止まっている and 前回と値が違う）で
    // 実際の絞り込み処理を行います。
    const subscription = this.filterTextSubject
      .asObservable()
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
      )
      .subscribe(v => {
        this.filterCodes(v)
      })

    this.$on('hook:beforeDestroy', () => {
      subscription.unsubscribe()
    })
  },
}

/**
 * ツリーノードの再帰処理
 */
function walk(entry, level, f) {
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, level + 1, f)
    }
  }
  f(entry, level)
}
</script>

<style lang="scss" scoped>
.wrapper {
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.actions {
  display: flex;
  border-top: 1px solid var(--color-border);
  align-items: center;
  padding: var(--spacing-small);

  .action-component {
    margin-left: var(--spacing-small);
  }
}

.tree-view-container {
  padding: var(--spacing-small);
  overflow: auto;
}
</style>
